import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MaterialModule } from 'src/app/shared/material.module';
import { LoggerService } from 'src/app/shared/services/logger.service';
import { SnackBarService } from 'src/app/shared/services/snack-service.service';
import { mapTemplateError } from '../template-error';
import {
  SyncState,
  buildSyncRequest,
  canSubmitSync,
  countOf,
  initialSyncState,
  selectedAddCount,
  selectedBilledCount,
  setConfirmBilled,
  toggleAddition,
  toggleAllAdditions,
  toggleDeleted,
  toggleRename,
} from '../sync-selection';
import { SyncPreview, SyncResult } from '../work-classification-template.models';
import { WorkClassificationTemplateService } from '../work-classification-template.service';

export interface SyncTemplateDialogData {
  projectId: number;
  projectName?: string | null;
  templateId: number;
  templateName?: string | null;
}

const T = 'WORKCLASSIFICATION.TEMPLATES.';

/**
 * "Päivitä mallista": previews and applies a template's changes to a linked tree. Nothing is ever deleted and the
 * site's own nodes are untouched. The request always carries an explicit selection.
 */
@Component({
  selector: 'app-sync-template-dialog',
  templateUrl: './sync-template-dialog.component.html',
  styleUrls: ['./sync-template-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [MaterialModule, TranslatePipe],
})
export class SyncTemplateDialogComponent implements OnInit {
  readonly data = inject<SyncTemplateDialogData>(MAT_DIALOG_DATA);
  private dialogRef = inject<MatDialogRef<SyncTemplateDialogComponent, SyncResult>>(MatDialogRef);
  private service = inject(WorkClassificationTemplateService);
  private snackBar = inject(SnackBarService);
  private translate = inject(TranslateService);
  private logger = inject(LoggerService);
  private destroyRef = inject(DestroyRef);

  preview = signal<SyncPreview | null>(null);
  state = signal<SyncState>({ add: new Set(), rename: new Set(), deleted: new Set(), confirmBilled: false });
  loading = signal(true);
  /** Set when the preview itself failed; holds the translation key to show. */
  loadError = signal<string | null>(null);
  saving = signal(false);
  /** Highlights the billed-rename confirmation after a BILLED_RENAME_NOT_CONFIRMED refusal. */
  confirmError = signal(false);

  nothingToDo = computed(() => {
    const p = this.preview();
    return !!p && p.additions.length === 0 && p.renames.length === 0 && p.deletedInProject.length === 0;
  });
  addCount = computed(() => selectedAddCount(this.state()));
  renameCount = computed(() => this.state().rename.size);
  billedCount = computed(() => {
    const p = this.preview();
    return p ? selectedBilledCount(p, this.state()) : 0;
  });
  allAdditionsSelected = computed(() => {
    const p = this.preview();
    return !!p && p.additions.length > 0 && this.state().add.size === p.additions.length;
  });
  canSubmit = computed(() => {
    const p = this.preview();
    return !!p && !this.saving() && canSubmitSync(p, this.state());
  });

  readonly countOf = countOf;

  ngOnInit(): void {
    this.service
      .syncPreview(this.data.templateId, this.data.projectId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: preview => {
          this.preview.set(preview);
          this.state.set(initialSyncState(preview));
          this.loading.set(false);
        },
        error: err => {
          this.loading.set(false);
          const m = mapTemplateError(err, { TEMPLATE_INACTIVE: T + 'SYNC.TEMPLATE_INACTIVE' });
          this.loadError.set(m.key);
          this.logger.errorWithContext('SyncTemplateDialog', 'Failed to load sync preview', err);
        },
      });
  }

  isAdd(id: number): boolean {
    return this.state().add.has(id);
  }
  isRename(id: number): boolean {
    return this.state().rename.has(id);
  }
  isDeleted(id: number): boolean {
    return this.state().deleted.has(id);
  }

  onToggleAddition(id: number, checked: boolean): void {
    this.state.set(toggleAddition(this.preview()!, this.state(), id, checked));
  }
  onToggleAllAdditions(checked: boolean): void {
    this.state.set(toggleAllAdditions(this.preview()!, this.state(), checked));
  }
  onToggleRename(id: number, checked: boolean): void {
    this.state.set(toggleRename(this.preview()!, this.state(), id, checked));
    if (this.billedCount() === 0) {
      this.confirmError.set(false);
    }
  }
  onToggleDeleted(id: number, checked: boolean): void {
    this.state.set(toggleDeleted(this.state(), id, checked));
  }
  onConfirmBilled(checked: boolean): void {
    this.state.set(setConfirmBilled(this.state(), checked));
    if (checked) {
      this.confirmError.set(false);
    }
  }

  apply(): void {
    const preview = this.preview();
    if (!preview || !this.canSubmit()) {
      return;
    }
    this.saving.set(true);
    this.service
      .sync(this.data.templateId, this.data.projectId, buildSyncRequest(preview, this.state()))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: result => {
          this.saving.set(false);
          this.dialogRef.close(result);
        },
        error: err => {
          this.saving.set(false);
          const m = mapTemplateError(err, { TEMPLATE_INACTIVE: T + 'SYNC.TEMPLATE_INACTIVE' });
          if (m.code === 'BILLED_RENAME_NOT_CONFIRMED') {
            this.confirmError.set(true);
          }
          this.snackBar.error(this.translate.instant(m.key, m.params));
        },
      });
  }
}
